import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useSubscription } from "@/context/SubscriptionContext";
import { getToken, login as authLogin } from "@/utils/auth";
import MaterialUsageDialog from "@/components/MaterialUsageDialog";
import {
  ArrowLeft, Search, Factory, Scissors, FileText, Layers,
  LogIn, ChevronRight, Lock, Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Material Usage entry page (Core flow).
 * Flow: Admin Login -> WO list -> machine picker -> MaterialUsageDialog.
 * No clock-in/out. Reuses the shared MaterialUsageDialog.
 */

const MACHINES = [
  { key: "process1",  machine_id: "M001",  label: "Corrugation",   icon: Factory,
    color: "from-orange-500 to-amber-600", desc: "Corrugation Machine" },
  { key: "process2a", machine_id: "M002A", label: "Sheet Cutting", icon: Scissors,
    color: "from-cyan-500 to-blue-600",    desc: "Sheet Cutting Machine" },
  { key: "process2b", machine_id: "M002B", label: "Paper Cutting", icon: FileText,
    color: "from-purple-500 to-fuchsia-600", desc: "Paper Cutting Machine" },
  { key: "process3",  machine_id: "M003",  label: "Sheet Pasting", icon: Layers,
    color: "from-emerald-500 to-teal-600", desc: "Sheet Pasting Machine" },
];

export default function MaterialUsagePage() {
  const navigate = useNavigate();
  const { ready, package: pkg } = useSubscription();
  const [hasToken, setHasToken] = useState(!!getToken());

  // Admin login state
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Data
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Step state
  const [selectedWO, setSelectedWO] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [muOpen, setMuOpen] = useState(false);

  useEffect(() => {
    if (ready && pkg !== "core") {
      toast.error("Material Usage is available on the Core package only.");
    }
  }, [ready, pkg]);

  useEffect(() => {
    if (!hasToken) return;
    loadWorkOrders();
  }, [hasToken]);

  const loadWorkOrders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/work-orders`);
      const active = (data || []).filter((w) => {
        const status = (w.status || "").toLowerCase();
        return status !== "archived" && status !== "delivered";
      });
      setWorkOrders(active);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load work orders");
    } finally {
      setLoading(false);
    }
  };

  const filteredWOs = useMemo(() => {
    if (!search.trim()) return workOrders;
    const q = search.trim().toLowerCase();
    return workOrders.filter((w) => {
      const num = (w.header?.work_order_number || w.wo_number || "").toLowerCase();
      const cust = (w.header?.customer_name || w.customer_name || "").toLowerCase();
      const item = (w.header?.item_name || w.item_name || "").toLowerCase();
      return num.includes(q) || cust.includes(q) || item.includes(q);
    });
  }, [workOrders, search]);

  const handleLogin = async () => {
    if (!loginUser || !loginPass) { toast.error("Enter username and password"); return; }
    setLoginLoading(true);
    try {
      const res = await authLogin(loginUser, loginPass, "admin");
      if (res.token) {
        setHasToken(true);
        setLoginOpen(false);
        setLoginUser(""); setLoginPass("");
        toast.success("Logged in");
      } else {
        toast.error("Login flow incomplete — try the main Login page");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const openMaterialDialog = (wo, machine) => {
    const procs = wo.processes || {};
    if (!procs[machine.key]) {
      toast.error(`This work order doesn't include ${machine.label}`);
      return;
    }
    setSelectedWO(wo);
    setSelectedMachine(machine);
    setMuOpen(true);
  };

  const handleMUSuccess = () => {
    setMuOpen(false);
    setSelectedMachine(null);
    setSelectedWO(null);
    loadWorkOrders();
  };

  // ----- Core-only gate -----
  if (ready && pkg !== "core") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 to-gray-900 p-6">
        <div className="max-w-sm w-full bg-gray-900/80 border border-amber-500/30 rounded-2xl p-8 text-center space-y-4"
             data-testid="mu-core-only">
          <Lock className="h-12 w-12 mx-auto text-amber-400" />
          <h2 className="text-2xl font-bold text-white">Core-only Feature</h2>
          <p className="text-gray-300 text-sm">
            Material Usage entry is part of the <b className="text-amber-300">Core</b> package. Pro users have the full Production Tracking + clock-in flow.
          </p>
          <Button onClick={() => navigate("/")} className="w-full bg-cyan-600 hover:bg-cyan-700">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  // ----- Admin login wall -----
  if (!hasToken) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-950 to-gray-900 p-6">
        <div className="max-w-sm w-full bg-gray-900/80 border border-cyan-500/30 rounded-2xl p-8 text-center space-y-5"
             data-testid="mu-login-wall">
          <div className="inline-flex p-4 rounded-full bg-cyan-500/10 border border-cyan-500/30 mx-auto">
            <Factory className="h-10 w-10 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Material Usage</h1>
            <p className="text-sm text-gray-400 mt-1">Enter balance material after machine completion</p>
            <p className="text-xs text-gray-500 mt-2">Admin login required</p>
          </div>
          <Button onClick={() => setLoginOpen(true)}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-base h-12"
                  data-testid="mu-open-login">
            <LogIn className="h-5 w-5 mr-2" /> Admin Login
          </Button>
          <button onClick={() => navigate("/")} className="text-sm text-gray-500 hover:text-gray-300">
            ← Back to Home
          </button>
        </div>

        <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
          <DialogContent className="bg-gray-900 border-cyan-500/30 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-cyan-300">Admin Login</DialogTitle>
              <DialogDescription className="text-gray-500 text-xs">
                Sign in as Admin to record material usage entries.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-gray-300 text-xs">Username</Label>
                <Input value={loginUser} onChange={(e) => setLoginUser(e.target.value)}
                       placeholder="admin"
                       className="bg-black/50 border-cyan-500/30 text-white mt-1"
                       data-testid="mu-login-username" />
              </div>
              <div>
                <Label className="text-gray-300 text-xs">Password</Label>
                <Input type="password" value={loginPass} onChange={(e) => setLoginPass(e.target.value)}
                       className="bg-black/50 border-cyan-500/30 text-white mt-1"
                       onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                       data-testid="mu-login-password" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleLogin} disabled={loginLoading}
                      className="w-full bg-cyan-600 hover:bg-cyan-700"
                      data-testid="mu-login-submit">
                {loginLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Login"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ----- Step 1: WO List -----
  if (!selectedWO) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 to-gray-900 pb-12">
        <div className="sticky top-0 z-10 bg-gray-950/90 backdrop-blur border-b border-cyan-500/20 px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="p-2 -ml-2 text-gray-400 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white">Material Usage</h1>
              <p className="text-xs text-gray-500">Select Work Order</p>
            </div>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input placeholder="Search WO #, customer, item..." value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   className="pl-9 bg-black/50 border-cyan-500/30 text-white h-11"
                   data-testid="mu-search" />
          </div>
        </div>

        <div className="px-4 py-4 space-y-3 max-w-2xl mx-auto">
          {loading && (
            <div className="text-center py-12 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading work orders...
            </div>
          )}
          {!loading && filteredWOs.length === 0 && (
            <div className="text-center py-12 text-gray-500" data-testid="mu-empty">No work orders found.</div>
          )}
          {!loading && filteredWOs.map((wo) => {
            const num = wo.header?.work_order_number || wo.wo_number || "—";
            const cust = wo.header?.customer_name || wo.customer_name || "—";
            const item = wo.header?.item_name || wo.item_name || "";
            const qty = wo.header?.order_qty || wo.order_qty || "";
            return (
              <button key={wo.id || num}
                      onClick={() => setSelectedWO(wo)}
                      className="w-full text-left bg-gray-900/70 border border-gray-700 hover:border-cyan-500/50 active:scale-[0.99] rounded-xl p-4 transition-all flex items-center gap-3"
                      data-testid={`mu-wo-${num}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-cyan-300 text-base">{num}</div>
                  <div className="text-sm text-gray-300 truncate mt-0.5">{cust}</div>
                  {item && <div className="text-xs text-gray-500 truncate mt-0.5">{item} {qty && `· ${qty} Nos`}</div>}
                </div>
                <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ----- Step 2: Machine Picker -----
  const woNum = selectedWO.header?.work_order_number || selectedWO.wo_number || "—";
  const cust = selectedWO.header?.customer_name || selectedWO.customer_name || "—";
  const procs = selectedWO.processes || {};
  const completed = new Set(selectedWO.completed_processes || []);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-gray-950 to-gray-900 pb-12">
        <div className="sticky top-0 z-10 bg-gray-950/90 backdrop-blur border-b border-cyan-500/20 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setSelectedWO(null)}
                  className="p-2 -ml-2 text-gray-400 hover:text-white"
                  data-testid="mu-back-to-list">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-cyan-300 truncate">{woNum}</h1>
            <p className="text-xs text-gray-500 truncate">{cust} · Select Machine</p>
          </div>
        </div>

        <div className="px-4 py-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
          {(() => {
            // Filter out machines whose process has already been completed.
            // Once data is entered for a machine, it should disappear from the picker
            // so the operator can't accidentally re-enter (and to keep the UI focused
            // on what's still pending).
            const visibleMachines = MACHINES.filter((m) => procs[m.key] && !completed.has(m.key));
            if (visibleMachines.length === 0) {
              return (
                <div className="col-span-full text-center py-12" data-testid="mu-all-done">
                  <div className="inline-flex w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 items-center justify-center mb-3">
                    <ChevronRight className="h-8 w-8 text-emerald-300" />
                  </div>
                  <p className="text-emerald-300 font-bold text-lg">All machines completed</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Material usage has been recorded for every machine on this work order.
                  </p>
                  <Button onClick={() => setSelectedWO(null)}
                          className="mt-5 bg-cyan-600 hover:bg-cyan-700"
                          data-testid="mu-pick-another-wo">
                    Pick another Work Order
                  </Button>
                </div>
              );
            }
            return visibleMachines.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.key}
                        onClick={() => openMaterialDialog(selectedWO, m)}
                        className="relative text-left rounded-xl p-5 transition-all border bg-gray-900/70 border-gray-700 hover:border-white/30 active:scale-[0.98]"
                        data-testid={`mu-machine-${m.key}`}>
                  <div className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${m.color} mb-3`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-base font-bold text-white">{m.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{m.desc}</div>
                </button>
              );
            });
          })()}
        </div>
      </div>

      <MaterialUsageDialog
        open={muOpen}
        onClose={() => { setMuOpen(false); setSelectedMachine(null); }}
        onSuccess={handleMUSuccess}
        workOrder={selectedWO}
        machineKey={selectedMachine?.key}
        machineId={selectedMachine?.machine_id}
        machineName={selectedMachine?.desc || selectedMachine?.label}
        employeeName="Admin"
      />
    </>
  );
}
